import React, { useCallback, useMemo, useState } from 'react';

import Model, { helper } from '@tripian/model';
import { PoiCategories, CloseIconButton } from '@tripian/react';

import SearchPlace from '../../../../../components/SearchPlace/SearchPlace';
import MustTryList from '../../../components/MustTryList/MustTryList';
import config from '../../../../../config';

import './SearchContainer.scss';
import IStepAlternatives from '../../../../../models/IStepAlternatives';

import useTrip from '../../../../../hooks/useTrip';
import useStep from '../../../../../hooks/useStep';
import useSearchPoi from '../../../../../hooks/useSearchPoi';
import useFocus from '../../../../../hooks/useFocus';

const attractionsCategoryIds = [1, 28, 30, 32, 26, 29, 25, 34];
const restaurantsCategoryIds = [3];
const cafesCategoryIds = [24, 36, 33];
const nightlifesCategoryIds = [4, 31, 35];

interface PlanIdStepDay {
  planId: number;
  step: Model.Step;
  dayIndex: number;
}

interface ISearchContainer {
  show: boolean;
  close: () => void;
  poiCategories: Model.PoiCategory[];
  dayIndex: number;
  plans: Model.Plan[];
  alternatives: IStepAlternatives[];
}

const SearchContainer: React.FC<ISearchContainer> = ({ show, close, poiCategories, dayIndex, plans, alternatives }) => {
  const [clickedTabPoiCategoryIds, setClickedTabPoiCategoryIds] = useState<number[]>(attractionsCategoryIds);

  const { tripReference } = useTrip();
  const { focusPoi, focusStep } = useFocus();
  const { searchPoi } = useSearchPoi();
  const { stepAdd, stepDelete } = useStep();

  const hasMustTry: boolean = tripReference !== undefined && tripReference.city.tastes.length > 0;

  const tabIndex = useCallback(() => {
    if (clickedTabPoiCategoryIds.includes(1)) return 0;
    if (clickedTabPoiCategoryIds.includes(3)) return 1;
    if (clickedTabPoiCategoryIds.includes(24)) return 2;
    if (clickedTabPoiCategoryIds.includes(4)) return 3;
    if (clickedTabPoiCategoryIds.includes(0)) return 4;
    return 0;
  }, [clickedTabPoiCategoryIds]);

  const currentPlanId = useMemo(() => (dayIndex < plans.length ? plans[dayIndex].id : 0), [dayIndex, plans]);

  const planStepDays: PlanIdStepDay[] = useMemo(() => {
    let steps: PlanIdStepDay[] = [];

    steps = plans.reduce((previousValue: PlanIdStepDay[], currentValue: Model.Plan, currentIndex: number) => {
      if (currentValue) {
        const currentSteps: PlanIdStepDay[] = currentValue.steps.map((step) => ({
          planId: currentValue.id,
          step,
          dayIndex: currentIndex,
        }));

        return previousValue.concat(currentSteps);
      }

      return previousValue;
    }, []);

    return steps;
  }, [plans]);

  const planAlternatives = useMemo(() => {
    const alternativePois = alternatives.reduce((previousValue: Model.Poi[], currentValue: IStepAlternatives) => previousValue.concat(currentValue.pois), []);

    const excludedEmpties = alternativePois.filter((alternativePoi) => alternativePoi);

    return helper.removeDuplicateValuesById<Model.Poi>(excludedEmpties);
  }, [alternatives]);

  const poiPlanStepDay = useCallback((poiId: number): PlanIdStepDay | undefined => planStepDays.find((memoizedStep) => memoizedStep.step.poi.id === poiId && memoizedStep.dayIndex === dayIndex), [dayIndex, planStepDays]);

  const isCurrentDayStep = useCallback((poiId: number): boolean => !!poiPlanStepDay(poiId), [poiPlanStepDay]);

  // for step poi dayIndexes like [0,1,2,...]
  // for alternative poi [-1]
  // not recommended poi [-2]
  const poiDayIndexes = useCallback(
    (poiId: number) => {
      const poisSteps: PlanIdStepDay[] = planStepDays.filter((memoizedStep: PlanIdStepDay) => memoizedStep.step.poi.id === poiId);

      // Multi plan poi part of day indexes
      if (poisSteps.length > 0) return poisSteps.map((poisStep) => poisStep.dayIndex); // for step poi dayIndexes like [0,1,2,...]

      // Only a plan part of day index
      return [planAlternatives.findIndex((alternativePoi) => alternativePoi.id === poiId) > -1 ? -1 : -2];
    },
    [planAlternatives, planStepDays],
  );

  const memoizedList = useCallback(
    (categoryIdsList: number[]) => {
      const recommendedCurrentCategorySteps: {
        planId: number;
        step: Model.Step;
        dayIndex: number;
      }[] = planStepDays.filter((memoizedStep) => categoryIdsList.includes(memoizedStep.step.poi.category[0].id));

      const recommendedCurrentCategoryStepPoisThisDay: Model.Poi[] = recommendedCurrentCategorySteps
        .filter((rs) => rs.dayIndex === dayIndex)
        .map((recommendedCurrentCategoryStep: { planId: number; step: Model.Step; dayIndex: number }) => recommendedCurrentCategoryStep.step.poi);

      const recommendedCurrentCategoryStepPoisOtherDays: Model.Poi[] = recommendedCurrentCategorySteps
        .filter((rs) => rs.dayIndex !== dayIndex)
        .map((recommendedCurrentCategoryStep: { planId: number; step: Model.Step; dayIndex: number }) => recommendedCurrentCategoryStep.step.poi);

      const recommendedCurrentCategoryAlternatives: Model.Poi[] = planAlternatives.filter((memoizedAlternative) => categoryIdsList.includes(memoizedAlternative.category[0].id));

      const resultList: Model.Poi[] = [...recommendedCurrentCategoryStepPoisThisDay, ...recommendedCurrentCategoryStepPoisOtherDays, ...recommendedCurrentCategoryAlternatives];

      return helper.removeDuplicateValuesById<Model.Poi>(resultList);
    },
    [dayIndex, planAlternatives, planStepDays],
  );

  const poiCategoriesClicked = (categoryIds: number[]) => {
    setClickedTabPoiCategoryIds(categoryIds);
  };

  const searchContainerClasses = ['searchContainer'];
  if (config.HIDE_OVERVIEW) searchContainerClasses.push('container-height');
  else searchContainerClasses.push('container-height-tab');
  if (show) searchContainerClasses.push('searchContainerOpen');
  else searchContainerClasses.push('searchContainerClose');

  const cardButtonOnClick = useCallback(
    (poi: Model.Poi) => {
      const planStepDay = poiPlanStepDay(poi.id);
      if (planStepDay) {
        stepDelete(planStepDay.step.id);
      } else {
        stepAdd(currentPlanId, poi.id);
      }
    },
    [currentPlanId, poiPlanStepDay, stepAdd, stepDelete],
  );

  const focus = useCallback(
    (poi: Model.Poi) => {
      const planStepDay = poiPlanStepDay(poi.id);
      if (planStepDay) focusStep(planStepDay.step);
      else focusPoi(poi);
    },
    [focusPoi, focusStep, poiPlanStepDay],
  );

  const renderContent = useMemo(() => {
    const index = tabIndex();

    let attractionsClassname = 'hide';
    let restaurantsClassname = 'hide';
    let cafesClassname = 'hide';
    let nightlifesClassname = 'hide';
    let mustTryListClassname = 'hide';

    if (index === 0) attractionsClassname = '';
    if (index === 1) restaurantsClassname = '';
    if (index === 2) cafesClassname = '';
    if (index === 3) nightlifesClassname = '';
    if (index === 4) mustTryListClassname = '';

    return (
      <div className="px4">
        {/* Attractions */}
        <div className={attractionsClassname}>
          <SearchPlace
            poiCategoryIds={attractionsCategoryIds}
            recommendedPois={memoizedList(attractionsCategoryIds)}
            search={searchPoi}
            focus={focus}
            cardButonOnClick={cardButtonOnClick}
            isCurrentDayStep={isCurrentDayStep}
            getDayIndexes={poiDayIndexes}
          />
        </div>

        {/* Restaurants */}
        <div className={restaurantsClassname}>
          <SearchPlace
            poiCategoryIds={restaurantsCategoryIds}
            recommendedPois={memoizedList(restaurantsCategoryIds)}
            search={searchPoi}
            focus={focus}
            cardButonOnClick={cardButtonOnClick}
            isCurrentDayStep={isCurrentDayStep}
            getDayIndexes={poiDayIndexes}
          />
        </div>

        {/* Cafes */}
        <div className={cafesClassname}>
          <SearchPlace poiCategoryIds={cafesCategoryIds} recommendedPois={memoizedList(cafesCategoryIds)} search={searchPoi} focus={focus} cardButonOnClick={cardButtonOnClick} isCurrentDayStep={isCurrentDayStep} getDayIndexes={poiDayIndexes} />
        </div>

        {/* Nightlife */}
        <div className={nightlifesClassname}>
          <SearchPlace
            poiCategoryIds={nightlifesCategoryIds}
            recommendedPois={memoizedList(nightlifesCategoryIds)}
            search={searchPoi}
            focus={focus}
            cardButonOnClick={cardButtonOnClick}
            isCurrentDayStep={isCurrentDayStep}
            getDayIndexes={poiDayIndexes}
          />
        </div>

        {/* Must Try */}
        {hasMustTry ? (
          <div className={`${mustTryListClassname} mb12`}>
            <MustTryList focus={focus} />
          </div>
        ) : null}
      </div>
    );
  }, [cardButtonOnClick, focus, hasMustTry, isCurrentDayStep, memoizedList, poiDayIndexes, searchPoi, tabIndex]);

  return (
    <div className={searchContainerClasses.join(' ')}>
      <div className="scrollable-y" style={{ height: '100%' }}>
        <div className="search-box-close mt3 pl3" style={{ display: 'flex' }}>
          <div className="search-close-icon m3">
            <CloseIconButton fill="#000" clicked={close} />
          </div>
        </div>
        <div style={{ width: 'calc(100% - 2rem)', margin: '1rem auto' }}>
          <PoiCategories poiCategories={poiCategories} click={poiCategoriesClicked} mustTry={hasMustTry} />
        </div>
        {renderContent}
      </div>
    </div>
  );
};

export default SearchContainer;
